import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress, TextField, Grid, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';

import { systemRequestsAPI, customersAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');
const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const NATIVE = { select: { native: true } };

function readBlobText(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('unreadable'));
    reader.readAsText(blob);
  });
}

async function blobError(err, fallback) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await readBlobText(data)).detail || fallback; } catch (e) { return fallback; }
  }
  return errText(err, fallback);
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function Field({ label, value }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ minHeight: 22, wordBreak: 'break-word' }}>{value || '-'}</Typography>
    </Grid>
  );
}

function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase' }}>{title}</Typography>
      {children}
    </Paper>
  );
}

const statusColor = (status) => (status === 'Complete' ? 'success' : 'info');

// ── Add / edit ─────────────────────────────────────────────────────────────

const EMPTY = { cust_code: '', environment: '', region: '', cl_number: '', vertical: '', product_version: '', tp_version: '',
  sdms_version: '', requested_by: '', requester_email: '', ticket_number: '', assignee_email: '', status: 'Infrastructure deployment' };

function RequestForm({ meta, request, onClose, onSaved }) {
  const editing = !!request;
  const [form, setForm] = useState(editing ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, request[k] || ''])) } : EMPTY);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) customersAPI.list().then((r) => setCustomers((r.data || []).filter((c) => c.is_active))).catch(() => setError('Could not load the customers'));
  }, [editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pickCustomer = (e) => {
    const c = customers.find((x) => x.cust_code === e.target.value);
    setForm((f) => ({ ...f, cust_code: e.target.value, cl_number: c?.cl_number || '', region: c?.location || '' }));
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const data = { ...form };
      if (editing) {
        ['cust_code', 'assignee_email', 'status'].forEach((k) => delete data[k]);
        await systemRequestsAPI.update(request.id, data);
        onSaved(request.id);
      } else {
        const res = await systemRequestsAPI.create(data);
        onSaved(res.data.id);
      }
    } catch (err) {
      setError(errText(err, 'Could not save the request'));
    } finally { setBusy(false); }
  };

  const ready = !!form.environment && !!form.ticket_number.trim() && (editing || !!(form.cust_code && form.assignee_email && form.status));
  const text = (k, label, extra = {}) => (
    <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" fullWidth label={label} value={form[k]} onChange={set(k)} {...extra} /></Grid>
  );

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit ${request.request_id}` : 'Add system request'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {editing
              ? <TextField size="small" fullWidth label="Customer" value={`${request.cust_code} - ${request.cust_name}`} disabled />
              : (
                <TextField select size="small" fullWidth label="Customer" value={form.cust_code} onChange={pickCustomer} required slotProps={NATIVE}>
                  <option value="" />
                  {customers.map((c) => <option key={c.id} value={c.cust_code}>{c.cust_code} - {c.cust_name}</option>)}
                </TextField>
              )}
          </Grid>
          {text('cl_number', 'CL number')}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select size="small" fullWidth label="Environment" value={form.environment} onChange={set('environment')} required slotProps={NATIVE}>
              <option value="" />
              {meta.environments.map((e) => <option key={e} value={e}>{e}</option>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select size="small" fullWidth label="Region" value={form.region} onChange={set('region')} slotProps={NATIVE}>
              <option value="" />
              {[...new Set([...meta.regions, form.region].filter(Boolean))].map((r) => <option key={r} value={r}>{r}</option>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select size="small" fullWidth label="Vertical" value={form.vertical} onChange={set('vertical')} slotProps={NATIVE}>
              <option value="" />
              {[...new Set([...meta.verticals, form.vertical].filter(Boolean))].map((v) => <option key={v} value={v}>{v}</option>)}
            </TextField>
          </Grid>
          {text('product_version', 'Product version')}
          {text('tp_version', 'TP version')}
          {text('sdms_version', 'SDMS version')}
          {text('requested_by', 'Requested by')}
          {text('requester_email', 'Requester email')}
          {text('ticket_number', 'Ticket #', { required: true })}
          {!editing && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select size="small" fullWidth label="Assignee" value={form.assignee_email} onChange={set('assignee_email')} required slotProps={NATIVE}>
                  <option value="" />
                  {meta.assignees.map((a) => <option key={a.email} value={a.email}>{a.name}</option>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select size="small" fullWidth label="Status" value={form.status} onChange={set('status')} required slotProps={NATIVE}>
                  {meta.stages.map((s) => <option key={s} value={s}>{s}</option>)}
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!ready || busy}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Status ─────────────────────────────────────────────────────────────────

function StatusDialog({ meta, request, onClose, onSaved }) {
  const [status, setStatus] = useState(request.status);
  const [assignee, setAssignee] = useState(request.assignee_email || '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const noDocs = status === 'Complete' && request.documents === 0;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await systemRequestsAPI.setStatus(request.id, { status, assignee_email: assignee || null, reason });
      onSaved();
    } catch (err) {
      setError(errText(err, 'Could not update the status'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Update status - {request.request_id}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} slotProps={NATIVE}>
            {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </TextField>
          <TextField select size="small" label="Assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} slotProps={NATIVE}>
            {!assignee && <option value="" />}
            {assignee && !meta.assignees.some((a) => a.email === assignee) && <option value={assignee}>{request.assignee_name}</option>}
            {meta.assignees.map((a) => <option key={a.email} value={a.email}>{a.name}</option>)}
          </TextField>
          <TextField size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} slotProps={{ htmlInput: { maxLength: 500 } }} />
          {status === 'Complete' && (
            <Alert severity={noDocs ? 'warning' : 'info'}>
              {noDocs
                ? 'Upload at least one document before completing this request.'
                : 'Completing the request locks it. It cannot be changed afterwards.'}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !assignee || noDocs}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Documents ──────────────────────────────────────────────────────────────

function DocumentsDialog({ meta, request, canChange, onClose, onChanged }) {
  const [docs, setDocs] = useState(null);
  const [environment, setEnvironment] = useState(request.environment || '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const locked = request.is_locked;

  const load = useCallback(async () => {
    try { setDocs((await systemRequestsAPI.documents(request.id)).data); }
    catch (err) { setError(errText(err, 'Could not load the documents')); setDocs([]); }
  }, [request.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(null);
    try { await systemRequestsAPI.upload(request.id, file, environment); await load(); onChanged(); }
    catch (err) { setError(errText(err, 'Could not upload the file')); }
    finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await systemRequestsAPI.download(request.id, d.id);
      saveBlob(res.data, d.original_filename);
    } catch (err) { setError(await blobError(err, 'Could not download the file')); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Documents - {request.request_id}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {request.cust_code} - {request.cust_name}. Files are kept in the customer's folder.
        </Typography>
        {canChange && !locked && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <TextField select size="small" label="Environment" value={environment} onChange={(e) => setEnvironment(e.target.value)}
              slotProps={NATIVE} sx={{ minWidth: 200 }}>
              <option value="" />
              {meta.environments.map((v) => <option key={v} value={v}>{v}</option>)}
            </TextField>
            <Button variant="contained" disabled={!environment || busy} onClick={() => fileRef.current.click()}>Upload</Button>
            <input ref={fileRef} type="file" hidden onChange={upload} />
            <Typography variant="caption" color="text.secondary">PDF, Word, Excel, PowerPoint, text, CSV or image, up to 25 MB</Typography>
          </Box>
        )}
        {locked && <Alert severity="info" sx={{ mb: 2 }}>This request is complete, so its documents are locked.</Alert>}
        {busy && <LinearProgress sx={{ mb: 1 }} />}
        {docs === null ? <CircularProgress size={24} /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Environment</TableCell><TableCell>File</TableCell><TableCell>Size</TableCell><TableCell>Uploaded</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell>{d.environment || '-'}</TableCell>
                    <TableCell>{d.original_filename}</TableCell>
                    <TableCell>{fmtSize(d.size_bytes)}</TableCell>
                    <TableCell>{fmtTime(d.uploaded_at)} by {d.uploaded_by}</TableCell>
                    <TableCell align="right"><Button size="small" onClick={() => download(d)}>Download</Button></TableCell>
                  </TableRow>
                ))}
                {docs.length === 0 && <TableRow><TableCell colSpan={5} align="center">No documents yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

function SystemRequests() {
  const { isAdmin } = usePermissions();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('open');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialog, setDialog] = useState(null);       // 'add' | 'edit' | 'status' | 'docs'
  const canChange = isAdmin();

  const loadList = useCallback(async (keepId) => {
    try {
      setLoading(true);
      const res = await systemRequestsAPI.list(state, search.trim());
      setRows(res.data);
      setError(null);
      const wanted = keepId || selectedId;
      if (!res.data.some((r) => r.id === wanted)) { setSelectedId(null); setDetail(null); }
    } catch (err) { setError(errText(err, 'Could not load the requests')); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, search]);

  useEffect(() => { systemRequestsAPI.meta().then((r) => setMeta(r.data)).catch((err) => setError(errText(err, 'Could not load the request options'))); }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async (id) => {
    try { setDetail((await systemRequestsAPI.get(id)).data); }
    catch (err) { setError(errText(err, 'Could not load the request')); }
  }, []);

  const select = (id) => { setSelectedId(id); setDetail(null); loadDetail(id); };

  const saved = async (id, message) => {
    setDialog(null);
    setSuccess(message);
    await loadList(id);
    setSelectedId(id);
    loadDetail(id);
  };

  if (!meta) return error ? <Alert severity="error">{error}</Alert> : <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">System Requests</Typography>
        {canChange && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog('add')}>Add</Button>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!canChange && <Alert severity="info" sx={{ mb: 2 }}>You can view requests and download their documents. Changes are made by Cloud Services administrators.</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField select size="small" label="Show" value={state} onChange={(e) => setState(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 130 }}>
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                <option value="all">All</option>
              </TextField>
              <TextField size="small" fullWidth label="Search request, customer, CL or ticket" value={search} onChange={(e) => setSearch(e.target.value)} />
            </Box>
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell><TableCell>Request</TableCell><TableCell>CL #</TableCell><TableCell>Ticket #</TableCell>
                    <TableCell>Customer</TableCell><TableCell>Requested</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover selected={r.id === selectedId} onClick={() => select(r.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Chip size="small" color={statusColor(r.status)} label={r.status === 'Complete' ? 'Complete' : 'Open'} icon={r.is_locked ? <LockIcon /> : undefined} />
                      </TableCell>
                      <TableCell><strong>{r.request_id}</strong></TableCell>
                      <TableCell>{r.cl_number || '-'}</TableCell>
                      <TableCell>{r.ticket_number || '-'}</TableCell>
                      <TableCell>{r.cust_code} - {r.cust_name}</TableCell>
                      <TableCell>{fmtDate(r.request_date)}</TableCell>
                    </TableRow>
                  ))}
                  {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} align="center">No requests found</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            {loading && <LinearProgress sx={{ mt: 1 }} />}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          {!selectedId && <Paper sx={{ p: 3 }}><Typography color="text.secondary">Select a request to see its details.</Typography></Paper>}
          {selectedId && !detail && <CircularProgress />}
          {detail && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>{detail.request_id}</Typography>
                <Chip size="small" color={statusColor(detail.status)} label={detail.status} icon={detail.is_locked ? <LockIcon /> : undefined} />
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" onClick={() => setDialog('docs')}>View documents ({detail.documents})</Button>
                {canChange && !detail.is_locked && <Button variant="outlined" onClick={() => setDialog('edit')}>Edit</Button>}
                {canChange && !detail.is_locked && <Button variant="contained" onClick={() => setDialog('status')}>Update status</Button>}
              </Box>
              {detail.is_locked && <Alert severity="info" sx={{ mb: 2 }}>This request was completed {fmtDate(detail.completed_at)} and is locked.</Alert>}
              {!detail.has_customer && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Customer {detail.cust_code} is not in the portal yet. Add the customer before uploading documents for this request.
                </Alert>
              )}

              <Section title="Request details">
                <Grid container spacing={1}>
                  <Field label="Request ID" value={detail.request_id} />
                  <Field label="Customer code" value={detail.cust_code} />
                  <Field label="CL number" value={detail.cl_number} />
                  <Field label="Customer name" value={detail.cust_name} />
                </Grid>
              </Section>
              <Section title="Product details">
                <Grid container spacing={1}>
                  <Field label="Region" value={detail.region} />
                  <Field label="Product" value={detail.product_version} />
                  <Field label="Environment" value={detail.environment} />
                  <Field label="Technology (TP)" value={detail.tp_version} />
                  <Field label="Vertical" value={detail.vertical} />
                  <Field label="SDMS" value={detail.sdms_version} />
                </Grid>
              </Section>
              <Section title="Requester details">
                <Grid container spacing={1}>
                  <Field label="Created by" value={detail.requested_by} />
                  <Field label="Email" value={detail.requester_email} />
                  <Field label="Request date" value={fmtDate(detail.request_date)} />
                  <Field label="Ticket #" value={detail.ticket_number} />
                </Grid>
              </Section>
              <Section title="Request status">
                <Grid container spacing={1}>
                  <Field label="Assigned to" value={detail.assignee_name} />
                  <Field label="Status" value={detail.status} />
                </Grid>
              </Section>
              <Section title="Request history">
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Assignee</TableCell><TableCell>Status</TableCell><TableCell>Reason</TableCell><TableCell>Last update</TableCell></TableRow></TableHead>
                    <TableBody>
                      {detail.history.map((h) => (
                        <TableRow key={h.id}>
                          <TableCell>{h.assignee_name || '-'}</TableCell>
                          <TableCell>{h.status}</TableCell>
                          <TableCell>{h.reason || '-'}</TableCell>
                          <TableCell>{fmtTime(h.changed_at)}</TableCell>
                        </TableRow>
                      ))}
                      {detail.history.length === 0 && <TableRow><TableCell colSpan={4} align="center">No history</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Section>
            </Box>
          )}
        </Grid>
      </Grid>

      {dialog === 'add' && <RequestForm meta={meta} onClose={() => setDialog(null)} onSaved={(id) => saved(id, 'Request created')} />}
      {dialog === 'edit' && detail && <RequestForm meta={meta} request={detail} onClose={() => setDialog(null)} onSaved={(id) => saved(id, 'Request updated')} />}
      {dialog === 'status' && detail && <StatusDialog meta={meta} request={detail} onClose={() => setDialog(null)} onSaved={() => saved(detail.id, 'Status updated')} />}
      {dialog === 'docs' && detail && (
        <DocumentsDialog meta={meta} request={detail} canChange={canChange} onClose={() => setDialog(null)}
          onChanged={() => { loadList(detail.id); loadDetail(detail.id); }} />
      )}
    </Box>
  );
}

export default SystemRequests;
